import {
    BUY_IN_WAREHOUSE_FACET_VALUE,
    DELIVERY_FACET_VALUE,
    GRS_ATTRIBUTE_FACET_KEY_PREFIX,
    GRS_BOPIM_FACET_KEY,
    GRS_BOPIW_FACET_KEY,
    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
    GRS_CA_PRICE_FACET_KEY,
    GRS_DELIVERY_FACET_KEY,
    GRS_RATING_FACET_KEY,
    GRS_REFINE_QUERY_PARAM,
    GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
    GRS_TWO_DAY_FACET_KEY,
    GRS_US_PRICE_FACET_KEY,
    LW_ATTRIBUTE_FACET_KEY_SUFFIX,
    LW_BOPIM_FACET_KEY,
    LW_BOPIW_FACET_KEY,
    LW_BRANDS_FACET_KEY,
    LW_BUY_IN_WAREHOUSE_FACET_KEY,
    LW_DELIVERY_FACET_KEY,
    LW_OUT_OF_STOCK_QUERY_PARAM_VALUE,
    LW_PRICE_FACET_KEY,
    LW_RATING_FACET_KEY,
    LW_REFINE_QUERY_PARAM,
    LW_SHOW_OUT_OF_STOCK_FACET_KEY,
    LW_TWO_DAY_FACET_KEY,
    RATING_LABEL_VALUE_KEY,
    REFINE_QUERY_PARAM,
    TWO_DAY_FACET_VALUE,
} from '@/components/SearchResultsFacets/constants';
import { GRS_BRANDS_FACET_KEY } from '@/components/SearchResultsFacets/grs.constants';
import { isGRSPriceFacetKey } from '@/components/SearchResultsFacets/grs.lib';
import {
    fullyDecodeURI,
    getSingleNonDefaultRefinementLabel,
    splitRefinementPairs,
} from '@/components/SearchResultsFacets/utils';
import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import {
    CATEGORY_PATH_PREFIX,
    GRS_REFINE_FILTER_KEY_VALUE_DELIMITER,
    LW_REFINE_FILTER_KEY_VALUE_DELIMITER,
    REFINE_QUERY_DELIMITER,
    SEARCH_PATH_PREFIX,
} from '@/constants/index';
import {
    ITEM_LOCATION_PRICING_FACET_KEY,
    ITEM_PROGRAM_FACET_KEY,
    ITEM_RATING_FACET_KEY,
} from '@/constants/index';
import { GRSFacetConfig, GRSFacetConfigProperties } from '@/types/contentStack';
import { isLocalDev } from '@/utils/index';
import { splitByCommaOutsideParentheses } from '@/utils/searchFacets';
import {
    type ComposableWindowArray,
    type ContentStackEntryDataProps,
    getComposableTextByDevFieldKey,
    normalizeCategoryLink,
    useBrowseContext,
} from '@nxt-costco-com/forge-digital-components';

import {
    getFacetKeyFromQueryParamKey,
    getGRSFacetsFromConfig,
} from './services/Grs';
import type {
    SearchResult,
    SearchServiceProps,
    WarehouseCookieValue,
} from './types';

/**
 * Take the index of the item returned from LW to calculate the current page
 * used in pagination navigation
 */
export function getCurrentPage(start: number, resultsPerPage: number) {
    if (start <= 0 || typeof start !== 'number') {
        return 1;
    }
    return Math.ceil(start / resultsPerPage) + 1;
}

export function getNumberOfPages(totalDocs: number, resultsPerPage: number) {
    return Math.ceil(totalDocs / resultsPerPage);
}

/**
 * Search URL includes a page parameter, but LW uses a start at item param,
 * so this maps from a page # to the first item on that page
 */
export function getFirstItemFromPageNumber(
    currentPage: number,
    resultsPerPage: number,
) {
    if (currentPage <= 1 || typeof currentPage !== 'number') {
        return 0;
    }
    return currentPage * resultsPerPage - resultsPerPage;
}

export function isBDSite(site: string | undefined) {
    return site?.slice(-2).toLowerCase() === 'bd';
}

export function getUserLocationFromDeliveryLocations({
    deliveryLocation,
    bdDeliveryLocation,
    site,
    lang,
}: {
    deliveryLocation?: { state?: string } | null;
    bdDeliveryLocation?: { bdLocation?: string } | null;
    site?: string;
    lang?: string;
}) {
    const userLocation = deliveryLocation?.state;

    if (!isBDSite(site)) {
        return userLocation;
    }

    if (/[a-zA-Z]/.test(bdDeliveryLocation?.bdLocation ?? '')) {
        // State Location is valid, use the last two characters as state code
        return bdDeliveryLocation?.bdLocation?.slice(-2);
    }

    if (lang === 'en-ca' || lang === 'fr-ca') {
        // For Canada BD, we cannot use the default value. If state code is not valid, we set it to undefined
        return undefined;
    }

    return userLocation;
}

export function getUseZipCodeFromDeliveryLocations({
    deliveryLocation,
    bdDeliveryLocation,
    site,
}: {
    deliveryLocation: ReturnType<typeof useBrowseContext>['deliveryLocation'];
    bdDeliveryLocation: ReturnType<
        typeof useBrowseContext
    >['bdDeliveryLocation'];
    site: string;
}) {
    const { zipCode: bcZipCode } = deliveryLocation || {};
    const { zipCode: bdZipCode } = bdDeliveryLocation || {};
    return isBDSite(site) ? bdZipCode : bcZipCode;
}

export function getLocationsFromCookie(
    warehouseCookieValue: WarehouseCookieValue,
) {
    const defaultReturnValue = ['*'];

    if (!warehouseCookieValue) {
        return { loc: defaultReturnValue.join(','), whloc: '', mdo: '' };
    }
    try {
        let returnValues = [];

        if (warehouseCookieValue.groceryCenters) {
            returnValues.push(...warehouseCookieValue.groceryCenters);
        }
        if (warehouseCookieValue.nearestWarehouse?.catalog) {
            returnValues.push(warehouseCookieValue.nearestWarehouse.catalog);
        }

        if (warehouseCookieValue.distributionCenters) {
            returnValues.push(...warehouseCookieValue.distributionCenters);
        }

        // If we don't have a distribution centers, the other centers don't
        // give us any value, so default to all
        if (
            !warehouseCookieValue.distributionCenters ||
            !warehouseCookieValue.distributionCenters.length
        ) {
            returnValues = defaultReturnValue;
        }

        return {
            loc: returnValues.join(','),
            whloc: warehouseCookieValue?.nearestWarehouse?.catalog || '',
            mdo:
                warehouseCookieValue?.pickUpCenters &&
                warehouseCookieValue?.pickUpCenters.length > 0
                    ? warehouseCookieValue?.pickUpCenters[0]
                    : '',
        };
    } catch (e) {
        console.error(e);
        return { loc: defaultReturnValue.join(','), whloc: '' };
    }
}

/**
 * Used to filter out price refinements during search query building as they
 * need custom treatment to include multiple ranges
 */
export function isPriceRefinement(refinement: string) {
    return refinement
        .toLowerCase()
        .startsWith(ITEM_LOCATION_PRICING_FACET_KEY.toLowerCase());
}

// fq={!tag=<facet key>}<facet key>:("<filter value>")
export function generateFacetFilterParam(refinement: string) {
    const [key, value] = refinement.split(/-(.*)/s);

    return `&fq=${key}:("${encodeURIComponent(value!)}")`;
}

export function isAttrRefinement(refinement: string) {
    const [facetKey] = refinement.split(/-(.*)/s);
    if (!facetKey) {
        return false;
    }
    return facetKey.toLowerCase().endsWith('_attr');
}

export function isRatingRefinement(refinement: string) {
    return refinement.toLowerCase().startsWith(ITEM_RATING_FACET_KEY);
}

export function isProgramRefinement(refinement: string) {
    return refinement.toLowerCase().startsWith(ITEM_PROGRAM_FACET_KEY);
}

export function buildMultiSelectFacet(refinements: string[]): string {
    // Build a mapping of all values for each facet key
    const attrs: { [key: string]: string[] } = {};

    let facets = '';

    for (const attr of refinements) {
        const [facetKey, val] = attr.split(/-(.*)/s);
        if (!facetKey || !val) {
            continue;
        }

        if (!attrs[facetKey]) {
            attrs[facetKey] = [];
        }

        const modifiedVal =
            facetKey.toLowerCase() === 'delivery_type_attr'
                ? val.replace(/\+/g, ' ')
                : val;

        const valArray = splitByCommaOutsideParentheses(modifiedVal);

        valArray.forEach(value => {
            attrs[facetKey]!.push(value);
        });
    }

    // Apply collected facet keys to URL
    for (const facetKey in attrs) {
        facets += `&fq=${encodeURIComponent(`{!tag=${facetKey}}${facetKey}:("${attrs[facetKey]!.join('" OR "')}")`)}`;
    }

    return facets;
}

export const getParam = (params: URLSearchParams, param: string) => {
    return params.get(param) || '';
};

/**
 * Ensure that a locally-rendered category link includes the category prefix
 * when necessary. Server-rendered links may be relative (for example
 * "/electronics") which on a local dev site can fail to resolve to the
 * expected category path. This function leaves absolute URLs and already-
 * prefixed paths untouched.
 *
 * @param baseUrl - The generated category URL or path (may include querystring)
 * @param categoryPath - The canonical category prefix (for example `/c/electronics`)
 * @returns either the original `baseUrl` or a prefixed version when appropriate
 */
export function ensureCategoryPrefix(baseUrl: string, categoryPath?: string) {
    try {
        if (!categoryPath || !baseUrl) return baseUrl;
        const normalizedCategoryPath = categoryPath.replace(/\/$/, '');
        const pathPart = baseUrl.split('?')[0] || '';
        // Preserve absolute URLs
        if (/^https?:\/\//.test(pathPart)) return baseUrl;
        // If it's already prefixed with the normalized category or a /c path, do nothing
        if (
            pathPart.startsWith(normalizedCategoryPath) ||
            pathPart.startsWith('/c')
        ) {
            return baseUrl;
        }
        // If path begins with a slash, append without duplicating separators
        if (pathPart.startsWith('/')) {
            return `${normalizedCategoryPath}/${baseUrl.replace(/^\//, '')}`;
        }
        return `${normalizedCategoryPath}/${baseUrl}`;
    } catch {
        return baseUrl;
    }
}

export function prependCategoryUrl(
    category_path: string | undefined,
    url: string,
    queryParams: string | undefined,
    publishedCategories: string[],
    /** Clears all query parameters from the URL except keyword and sortBy, useful for navigating up the category tree */
    clearExtraParams: boolean,
) {
    const params = new URLSearchParams(queryParams);
    const ensuredURL = isLocalDev()
        ? ensureCategoryPrefix(url, category_path)
        : url;

    if (clearExtraParams) {
        // We want to preserve the keyword and sortBy param if any exists, but not the refine param as selections may not apply
        for (const [key] of params) {
            if (key !== 'keyword' && key !== 'sortBy') {
                params.delete(key);
            }
        }
        const qParams = params.toString();
        const finalURL = qParams
            ? `${ensuredURL}${ensuredURL.includes('?') ? '&' : '?'}${qParams}`
            : ensuredURL;
        return normalizeCategoryLink(
            category_path,
            finalURL,
            publishedCategories,
        );
    } else if (queryParams) {
        // If we're drilling down to a more specific category, we want to keep any applied filters
        return normalizeCategoryLink(
            category_path,
            `${ensuredURL}?${queryParams}`,
            publishedCategories,
        );
    }

    return normalizeCategoryLink(
        category_path,
        ensuredURL,
        publishedCategories,
    );
}

// Update page title with refinements
// If we have a single attribute param, add it to the page title
export function updatePageTitle({
    searchParams,
    searchResult,
    searchService,
    grsFacetConfig,
    pageTitle,
    facetConfig,
}: {
    searchParams: URLSearchParams;
    searchResult: SearchResult | undefined;
    searchService: SearchServiceProps;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    pageTitle?: string;
    facetConfig?: ContentStackEntryDataProps | undefined;
}) {
    const kw = searchParams.get('keyword') || '';
    let nonDefaultRefinementLabel = '';
    if (
        searchParams.get(REFINE_QUERY_PARAM) ||
        searchParams.get(GRS_REFINE_QUERY_PARAM)
    ) {
        const refinementKey =
            searchService === SEARCH_SERVICE_PROVIDERS.GRS
                ? GRS_REFINE_QUERY_PARAM
                : LW_REFINE_QUERY_PARAM;
        nonDefaultRefinementLabel = getSingleNonDefaultRefinementLabel({
            searchService,
            grsFacetConfig,
            refine: searchParams.get(refinementKey) || '',
            facetConfig,
        });
    }
    const titlePrefix = nonDefaultRefinementLabel
        ? `${nonDefaultRefinementLabel} `
        : '';
    if (kw) {
        document.title = `${titlePrefix}${kw} | Costco`;
    } else if (pageTitle) {
        document.title = `${titlePrefix}${pageTitle} | Costco`;
    } else if (searchResult?.breadcrumb) {
        const lastBreadcrumb = Array.isArray(searchResult?.breadcrumb)
            ? searchResult.breadcrumb.slice(-1)?.[0]?.name
            : undefined;
        document.title = lastBreadcrumb
            ? `${lastBreadcrumb} | Costco`
            : `Search | Costco`;
    } else {
        document.title = `Search | Costco`;
    }
}

export function isCategoryPath(path: string) {
    return path.startsWith(CATEGORY_PATH_PREFIX);
}

export function isSearchPath(path: string = '') {
    // Search page can be anything if ends with /s
    return path.toLowerCase().endsWith(SEARCH_PATH_PREFIX.toLowerCase());
}

export function generateEmptySearchResult(): SearchResult {
    return {
        isAdTargetingExceptionEnabled: false,
        isAdTargetingExplicitlyDisabled: false,
        isAdTargetingEnabled: false,
        redirect: '',
        docs: [],
        facets: [],
        variants: [],
        metrics: {
            original: undefined,
            correction: undefined,
            queryTime: 0,
            queryId: '',
            totalTime: 0,
            semantic: false,
            xFusionExitCode: '',
        },
        pagination: {
            currentPage: 0,
            totalDocs: 0,
            totalPages: 0,
        },
    };
}

/**
 * Converts GRS dynamic filter to LW dynamic filter format
 * @param str
 * @returns string
 * Example: attributes.color_format --> Color_Format_attr
 */
export const convertGRSDynamicFilterToLW = (str: string): string => {
    const newString = str
        .toLowerCase()
        .replace(GRS_ATTRIBUTE_FACET_KEY_PREFIX, '')
        .split('_')
        .map(word => {
            // For each word, capitalize the first letter and make the rest lowercase
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join('_');
    return `${newString}${LW_ATTRIBUTE_FACET_KEY_SUFFIX}`;
};

/**
 * Converts GRS refine filters to LW refine filters
 * @param grsFilterPairs Filters applied from the url in GRS format
 * @param grsFacetConfig Whitelisted GRS facets from content stack
 * @param filterMap standard filter mapping between LW and GRS
 * @returns updatedFilters Array of filters in LW format
 */
export const convertGrsToLw = (
    grsFilterPairs: string[],
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined,
    facetConfig: ContentStackEntryDataProps | undefined,
): string[] => {
    const updatedFilters: string[] = [];
    let grsFacetsMap: GRSFacetConfig = {};
    if (grsFacetConfig) {
        // Create queryParam to facet key mapping from GRS facet config
        grsFacetsMap = getGRSFacetsFromConfig(grsFacetConfig);
    }
    grsFilterPairs.forEach(grsFilterPair => {
        const [currentFilterKey, currentFilterValue] = splitRefinementPairs(
            grsFilterPair,
            GRS_REFINE_FILTER_KEY_VALUE_DELIMITER,
        );
        // const queryParamExistingValuesCommaSeparated =
        //     splitByCommaOutsideParentheses(currentFilterValue || '');

        if (currentFilterKey) {
            let updatedFilterValue = fullyDecodeURI(currentFilterValue);
            let mappedFilter: string | undefined;
            const grsFacetAttributeKey = getFacetKeyFromQueryParamKey(
                currentFilterKey,
                grsFacetsMap,
            );
            if (
                grsFacetAttributeKey === GRS_TWO_DAY_FACET_KEY ||
                grsFacetAttributeKey === GRS_DELIVERY_FACET_KEY
            ) {
                mappedFilter = LW_TWO_DAY_FACET_KEY || LW_DELIVERY_FACET_KEY;
            } else if (
                grsFacetAttributeKey === GRS_BUY_IN_WAREHOUSE_FACET_KEY
            ) {
                mappedFilter = LW_BUY_IN_WAREHOUSE_FACET_KEY;
                updatedFilterValue = BUY_IN_WAREHOUSE_FACET_VALUE;
            } else if (grsFacetAttributeKey === GRS_BOPIM_FACET_KEY) {
                mappedFilter = LW_BOPIM_FACET_KEY;
            } else if (grsFacetAttributeKey === GRS_BOPIW_FACET_KEY) {
                mappedFilter = LW_BOPIW_FACET_KEY;
            } else if (
                grsFacetAttributeKey === GRS_SHOW_OUT_OF_STOCK_FACET_KEY
            ) {
                mappedFilter = LW_SHOW_OUT_OF_STOCK_FACET_KEY;
                updatedFilterValue = LW_OUT_OF_STOCK_QUERY_PARAM_VALUE;
            } else if (isGRSPriceFacetKey(grsFacetAttributeKey ?? '')) {
                mappedFilter = LW_PRICE_FACET_KEY;
                const currentPriceRangeValues = splitByCommaOutsideParentheses(
                    currentFilterValue || '',
                );
                const updatedPriceRangeValues: string[] =
                    currentPriceRangeValues.map(
                        (grsFormatPriceRangeValue: string) => {
                            const numbers: RegExpMatchArray | null =
                                grsFormatPriceRangeValue.match(/\d+/g);
                            return `${numbers?.[0]} TO ${numbers?.[1]}`;
                        },
                    );
                updatedFilterValue = updatedPriceRangeValues.join(',');
            } else if (grsFacetAttributeKey === GRS_RATING_FACET_KEY) {
                mappedFilter = LW_RATING_FACET_KEY;
                const currentRatingRangeValues = splitByCommaOutsideParentheses(
                    currentFilterValue || '',
                );
                const updatedRatingRangeValues: string[] =
                    currentRatingRangeValues
                        .map((grsFormatRatingRangeValue: string): string => {
                            const numbers: RegExpMatchArray | null =
                                grsFormatRatingRangeValue.match(/\d+/g);
                            const value = numbers?.[0] || '';
                            if (!value) return '';

                            const formattedRatingValue =
                                facetConfig &&
                                getComposableTextByDevFieldKey({
                                    devFieldKey: RATING_LABEL_VALUE_KEY,
                                    composableWindow:
                                        facetConfig.composable_window as ComposableWindowArray,
                                    templateVariables: {
                                        ratingMinimum: value?.toString(),
                                    },
                                });
                            return formattedRatingValue || `${value} & Up`;
                        })
                        .filter(Boolean);
                updatedFilterValue = updatedRatingRangeValues.join(',');
            } else if (grsFacetAttributeKey === GRS_BRANDS_FACET_KEY) {
                mappedFilter = LW_BRANDS_FACET_KEY;
            }

            if (!mappedFilter) {
                mappedFilter =
                    convertGRSDynamicFilterToLW(
                        grsFacetAttributeKey || currentFilterKey,
                    ) || '';
            }
            if (mappedFilter && updatedFilterValue) {
                updatedFilters.push(
                    `${mappedFilter}${LW_REFINE_FILTER_KEY_VALUE_DELIMITER}${updatedFilterValue}`,
                );
                // queryParamExistingValuesCommaSeparated.forEach(value => {
                //     const mappedFilterPair = `${mappedFilter}${LW_REFINE_FILTER_KEY_VALUE_DELIMITER}${value}`;
                //     updatedFilters.push(mappedFilterPair);
                // });
            }
        }
    });
    return updatedFilters;
};

/**
 * Converts LW dynamic facet key to GRS dynamic facet key format without attributes prefix
 * @param str LW dynamic facet key
 * @returns GRS dynamic facet key without attributes prefix
 */
const getGRSDynamicFacetKeyFromLWKey = (str: string): string => {
    return str
        .toLowerCase()
        .replace(LW_ATTRIBUTE_FACET_KEY_SUFFIX, '')
        .replace(/_+$/, ''); // remove trailing underscores
};

/**
 * Converts LW dynamic filter to GRS format with attributes prefix
 * @param str LW dynamic filter
 * @returns GRS dynamic filter with attributes prefix
 */
export const convertLWDynamicFilterToGRS = (str: string): string => {
    const newString = getGRSDynamicFacetKeyFromLWKey(str);
    return `${GRS_ATTRIBUTE_FACET_KEY_PREFIX}${newString}`;
};

/**
 * Converts LW refine filters to GRS refine filters
 * @param lwFilterPairs Filters applied from the url in LW format
 * @param grsFacetConfig Whitelisted GRS facets from content stack
 * @param filterMap standard filter mapping between LW and GRS
 * @returns Array of filters in GRS format
 */
export const convertLwToGrs = (
    lwFilterPairs: string[],
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined,
): string[] => {
    const updatedFilters: string[] = [];
    const reducedFilterKeys: string[] = [];
    lwFilterPairs.forEach(lwFilterPair => {
        const [currentFilterKey, _currentFilterValue] = splitRefinementPairs(
            lwFilterPair,
            LW_REFINE_FILTER_KEY_VALUE_DELIMITER,
        );
        // Considering logic to handle same filter added again with different values in LW whereas GRS requires all values to be in a single filter
        if (
            currentFilterKey &&
            !reducedFilterKeys?.includes(currentFilterKey)
        ) {
            reducedFilterKeys.push(currentFilterKey);
            let mappedFilter: string | undefined;
            const gatheredSimilarFilterPairs = lwFilterPairs.filter(
                filterKeyValuePair =>
                    filterKeyValuePair.startsWith(currentFilterKey),
            );
            const gatheredSameFilterValues = gatheredSimilarFilterPairs.map(
                filterKeyValuePair => {
                    const [, filterValue] = splitRefinementPairs(
                        filterKeyValuePair,
                        LW_REFINE_FILTER_KEY_VALUE_DELIMITER,
                    );
                    return filterValue;
                },
            );
            let requiredFilterValue: string | undefined =
                gatheredSameFilterValues.join(',');
            if (
                currentFilterKey === LW_TWO_DAY_FACET_KEY ||
                currentFilterKey === LW_DELIVERY_FACET_KEY ||
                currentFilterKey === LW_BUY_IN_WAREHOUSE_FACET_KEY
            ) {
                const hasTwoDay = requiredFilterValue
                    ?.toLowerCase()
                    .includes(TWO_DAY_FACET_VALUE.toLowerCase());
                const hasDelivery = requiredFilterValue
                    ?.toLowerCase()
                    .includes(DELIVERY_FACET_VALUE.toLowerCase());
                const hasInWarehouse = requiredFilterValue
                    ?.toLowerCase()
                    .includes(BUY_IN_WAREHOUSE_FACET_VALUE.toLowerCase());
                if (hasInWarehouse) {
                    mappedFilter =
                        grsFacetConfig?.properties?.[0]?.standard?.[
                            GRS_BUY_IN_WAREHOUSE_FACET_KEY
                        ]?.queryParamKey;
                    requiredFilterValue = undefined;
                    const mappedFilterPair = `${mappedFilter}${GRS_REFINE_FILTER_KEY_VALUE_DELIMITER}true`;
                    updatedFilters.push(mappedFilterPair);
                }
                if (hasDelivery || hasTwoDay) {
                    requiredFilterValue = undefined;
                    mappedFilter =
                        grsFacetConfig?.properties?.[0]?.attributes?.[
                            GRS_DELIVERY_FACET_KEY?.replace(
                                GRS_ATTRIBUTE_FACET_KEY_PREFIX,
                                '',
                            ) ||
                                GRS_TWO_DAY_FACET_KEY?.replace(
                                    GRS_ATTRIBUTE_FACET_KEY_PREFIX,
                                    '',
                                )
                        ]?.queryParamKey;
                    let valueString = '';
                    if (hasDelivery) {
                        valueString += DELIVERY_FACET_VALUE;
                    }
                    if (hasTwoDay) {
                        if (valueString.length > 0) {
                            valueString += ',';
                        }
                        valueString += TWO_DAY_FACET_VALUE;
                    }

                    const mappedFilterPair = `${mappedFilter}${GRS_REFINE_FILTER_KEY_VALUE_DELIMITER}${valueString}`;
                    updatedFilters.push(mappedFilterPair);
                }
            } else if (currentFilterKey === LW_BOPIM_FACET_KEY) {
                mappedFilter =
                    grsFacetConfig?.properties?.[0]?.standard?.[
                        GRS_BOPIM_FACET_KEY
                    ]?.queryParamKey;
            } else if (currentFilterKey === LW_BOPIW_FACET_KEY) {
                mappedFilter =
                    grsFacetConfig?.properties?.[0]?.standard?.[
                        GRS_BOPIW_FACET_KEY
                    ]?.queryParamKey;
            } else if (currentFilterKey === LW_SHOW_OUT_OF_STOCK_FACET_KEY) {
                mappedFilter =
                    grsFacetConfig?.properties?.[0]?.standard?.[
                        GRS_SHOW_OUT_OF_STOCK_FACET_KEY
                    ]?.queryParamKey;
                requiredFilterValue = 'true';
            } else if (currentFilterKey === LW_PRICE_FACET_KEY) {
                // Find the price facet config by trying known keys, then falling back to
                // a pattern search to support BD sites with dynamic warehouse IDs
                const standard =
                    grsFacetConfig?.properties?.[0]?.standard ?? {};
                const grsPriceFacetConfig =
                    standard[GRS_US_PRICE_FACET_KEY] ??
                    standard[GRS_CA_PRICE_FACET_KEY] ??
                    Object.entries(standard).find(([key]) =>
                        isGRSPriceFacetKey(key),
                    )?.[1];
                mappedFilter = grsPriceFacetConfig?.queryParamKey;
                const currentPriceRangeValues = splitByCommaOutsideParentheses(
                    requiredFilterValue || '',
                );
                const updatedPriceRangeValues = currentPriceRangeValues.map(
                    grsFormatPriceRangeValue => {
                        const numbers = grsFormatPriceRangeValue.match(/\d+/g);
                        return `(${numbers?.[0]},${numbers?.[1]})`;
                    },
                );
                requiredFilterValue = updatedPriceRangeValues.join(',');
            } else if (currentFilterKey === LW_RATING_FACET_KEY) {
                mappedFilter =
                    grsFacetConfig?.properties?.[0]?.standard?.[
                        GRS_RATING_FACET_KEY
                    ]?.queryParamKey;

                const currentRatingRangeValues = splitByCommaOutsideParentheses(
                    requiredFilterValue || '',
                );
                const updatedRatingRangeValues = currentRatingRangeValues.map(
                    grsFormatPriceRangeValue => {
                        const numbers = grsFormatPriceRangeValue.match(/\d+/g);
                        return `(${numbers?.[0]},${parseInt(numbers?.[0] || '0') + 1})`;
                    },
                );
                requiredFilterValue = updatedRatingRangeValues.join(',');
            } else if (currentFilterKey === LW_BRANDS_FACET_KEY) {
                mappedFilter =
                    grsFacetConfig?.properties?.[0]?.standard?.[
                        GRS_BRANDS_FACET_KEY
                    ]?.queryParamKey;
            }

            if (!mappedFilter) {
                const grsFilterKey =
                    getGRSDynamicFacetKeyFromLWKey(currentFilterKey);
                mappedFilter =
                    grsFacetConfig?.properties?.[0]?.attributes?.[grsFilterKey]
                        ?.queryParamKey;
                // For non-configured dynamic filters, treat them as attribute facets
                // by applying the GRS attribute prefix instead of using the bare key.
                if (!mappedFilter && grsFilterKey) {
                    mappedFilter = `${GRS_ATTRIBUTE_FACET_KEY_PREFIX}${grsFilterKey}`;
                }
            }
            if (mappedFilter && requiredFilterValue) {
                const mappedFilterPair = `${mappedFilter}${GRS_REFINE_FILTER_KEY_VALUE_DELIMITER}${requiredFilterValue}`;
                updatedFilters.push(mappedFilterPair);
            }
        }
    });
    return updatedFilters;
};

/**
 * Get required search params with converted refine filters based on search service provider
 * @param searchService Search Provider where the data is being fetched from
 * @param searchParams URLSearchParams object containing current search parameters
 * @param grsFacetConfig Whitelisted GRS facets from content stack
 * @returns Updated URLSearchParams with appropriate refine filters and a flag indicating if filters were changed
 */
export const getRequiredSearchParams = (
    searchService: SearchServiceProps,
    searchParams: URLSearchParams,
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined,
    facetConfig?: ContentStackEntryDataProps | undefined,
): { updatedSearchParams: URLSearchParams; filterChanged: boolean } => {
    const grsFilters = searchParams.get(GRS_REFINE_QUERY_PARAM);
    const lwFilters = searchParams.get(REFINE_QUERY_PARAM);

    let grsFiltersArray: string[] = [];
    let lwFiltersArray: string[] = [];
    let filterChanged = false;

    if (grsFilters) {
        grsFiltersArray =
            fullyDecodeURI(grsFilters)?.split(REFINE_QUERY_DELIMITER) || [];
    }

    if (lwFilters) {
        lwFiltersArray =
            fullyDecodeURI(lwFilters)?.split(REFINE_QUERY_DELIMITER) || [];
    }
    let convertedFiltersArray: string[] = [];

    if (
        searchService !== SEARCH_SERVICE_PROVIDERS.GRS &&
        grsFiltersArray.length > 0
    ) {
        // Convert GRS refine filters to LW format
        convertedFiltersArray = convertGrsToLw(
            grsFiltersArray,
            grsFacetConfig,
            facetConfig,
        );
    } else if (
        searchService === SEARCH_SERVICE_PROVIDERS.GRS &&
        lwFiltersArray.length > 0
    ) {
        // Convert LW refine filters to GRS format
        convertedFiltersArray = convertLwToGrs(lwFiltersArray, grsFacetConfig);
    } else {
        filterChanged = true;
    }

    const requiredRefineParam =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? GRS_REFINE_QUERY_PARAM
            : REFINE_QUERY_PARAM;

    const currentSupportedFiltersArray =
        searchService === SEARCH_SERVICE_PROVIDERS.GRS
            ? grsFiltersArray
            : lwFiltersArray;

    const updatedRefinementArray = [
        ...currentSupportedFiltersArray,
        ...convertedFiltersArray,
    ];
    const updatedSearchParams = new URLSearchParams(searchParams);

    // Remove old refine params and set the required refine param
    updatedSearchParams.delete(REFINE_QUERY_PARAM);
    updatedSearchParams.delete(GRS_REFINE_QUERY_PARAM);
    if (updatedRefinementArray?.length !== 0) {
        updatedSearchParams.set(
            requiredRefineParam,
            encodeURIComponent(
                updatedRefinementArray.join(REFINE_QUERY_DELIMITER),
            ),
        );
    }
    return { updatedSearchParams, filterChanged };
};

//
function productNameToUrl(productName: string): string {
    return productName.toLowerCase().replace(/[^a-z0-9]/g, '-');
}

/**
 * Redirect for Item Number, Product Number and Model Number Search to PDP if service provider is LucidWorks
 *
 * 1. Number of search results returned  “numFound" in API response must be 1 AND satisfy one of the following criteria's
 * 2. Search term exactly matches “item_number” from API response
 *
 * Returns false to do nothing, and a string if a redirect should be done
 */
export function checkForLucidWorksItemNumberRedirect(
    keyword: string | string[] | undefined | null,
    searchResult: SearchResult | undefined,
): false | string {
    if (!keyword || !searchResult) {
        return false;
    }
    if (Array.isArray(keyword)) {
        keyword = keyword[0] as string;
    }
    if (
        !keyword ||
        !searchResult ||
        !('docs' in searchResult) ||
        searchResult.docs.length !== 1
    ) {
        return false;
    }
    const item = searchResult.docs[0];

    if (
        item?.item_number === keyword ||
        item?.group_id === keyword ||
        (item &&
            'Model_attr' in item &&
            Array.isArray(item.Model_attr) &&
            (item.Model_attr as (string | number)[]).some(
                (attr: string | number) =>
                    attr.toString().toLowerCase() === keyword.toLowerCase(),
            ))
    ) {
        return `${productNameToUrl(item?.item_product_name ?? '')}.product.${item?.group_id}.html`;
    }

    return false;
}

/**
 * Redirect for Item Number, Product Number and Model Number Search to PDP if service provider is GRS
 *
 * 1. Number of search results returned  "totalSize" in API response must be 1 AND satisfy one of the following criteria's
 * 2. Search term exactly matches “item_number” or "group_id" from API response
 *
 * Returns false to do nothing, and a string if a redirect should be done
 */
export function checkForGrsItemNumberRedirect(
    keyword: string | string[] | undefined | null,
    searchResult: SearchResult | undefined,
): false | string {
    if (!keyword || !searchResult) {
        return false;
    }

    if (Array.isArray(keyword)) {
        keyword = keyword[0] as string;
    }
    const grsResult =
        searchResult && 'searchResult' in searchResult
            ? searchResult.searchResult
            : undefined;
    if (!keyword || grsResult?.totalSize !== 1) {
        return false;
    }
    const item = grsResult?.results[0];
    const itemGroupId = item?.id;
    const productName = item?.product?.title;
    const productVariants = item?.product?.variants;
    // Skip redirections on travel and pharmacy items
    if (
        item?.product?.attributes?.product_type?.text?.[0]?.toLowerCase() !==
        'product'
    ) {
        return false;
    }
    // looping through all the variants available in the response and comparing it with the keyword value
    const hasMatchingItemNumber = productVariants?.some(
        (variant: { id: string }) => {
            return (
                variant.id?.toString().toLowerCase() === keyword.toLowerCase()
            );
        },
    );
    if (
        hasMatchingItemNumber ||
        itemGroupId === keyword ||
        item?.product?.attributes?.model?.text?.[0]?.toLowerCase() ===
            keyword.toLowerCase()
    ) {
        return `${productNameToUrl(productName ?? '')}.product.${itemGroupId}.html`;
    }

    return false;
}

export function getPageIdFromPath(path: string | undefined | null) {
    if (!path) {
        return;
    }

    const pageId = path.split('/').at(-1);

    return pageId?.replace('.html', '');
}
